import { Phone, Mail, MapPin, Clock, Printer } from "lucide-react";
import Reveal from "@/components/Reveal";
import ConsultationForm from "@/components/ConsultationForm";
import Seo from "@/components/Seo";
import { FIRM } from "@/lib/site";
import { ServingMassachusetts } from "./Home";

export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-white">
      <Seo
        title="Contact | Free Consultation — Mahaney Law"
        description="Request a free consultation with Massachusetts trial attorney Joseph M. Mahaney. Serving all of Massachusetts. Every inquiry is reviewed personally."
        path="/contact"
      />

      <section className="bg-navy-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <div className="eyebrow text-gold">Contact</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.05] tracking-tight text-navy-900 mt-5 max-w-4xl text-balance">
              Request a free consultation.
            </h1>
            <p className="mt-7 max-w-2xl text-lg md:text-[1.15rem] text-navy-900/65 leading-relaxed font-light">
              Tell us about your matter. Attorney Mahaney reviews each new inquiry
              personally. Submitting this form opens your email app with a
              pre-filled message addressed to our office. {FIRM.serviceArea}.
            </p>
            <div className="divider-gold mt-8" />
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Form */}
            <div className="lg:col-span-7">
              <Reveal>
                <ConsultationForm testId="contact-form" />
              </Reveal>
            </div>

            {/* Info */}
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-sm ink-gradient grain border border-white/10 text-white p-8 md:p-10">
                  <div className="eyebrow text-gold">Mahaney Law</div>
                  <h2 className="font-display text-3xl mt-3 leading-tight">
                    Reach the firm directly.
                  </h2>
                  <div className="mt-8 space-y-6">
                    <Info
                      icon={Phone}
                      label="Phone"
                      value={FIRM.phone}
                      href={`tel:${FIRM.phoneHref}`}
                      testId="info-phone"
                    />
                    <Info
                      icon={Printer}
                      label="Fax"
                      value={FIRM.fax}
                      testId="info-fax"
                    />
                    <Info
                      icon={Mail}
                      label="Email"
                      value={FIRM.email}
                      href={`mailto:${FIRM.email}`}
                      testId="info-email"
                    />
                    <Info
                      icon={MapPin}
                      label="Office"
                      value={`${FIRM.address.line1}, ${FIRM.address.line2}`}
                      testId="info-office"
                    />
                    <Info
                      icon={Clock}
                      label="Hours"
                      value={FIRM.hours}
                      testId="info-hours"
                    />
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.15}>
                <div
                  data-testid="contact-map"
                  className="mt-6 rounded-sm border border-navy-900/10 bg-navy-50 overflow-hidden aspect-[16/11]"
                >
                  <iframe
                    title="Mahaney Law office location"
                    data-testid="contact-map-iframe"
                    src="https://www.google.com/maps?q=932%20Concord%20St%2C%20Framingham%2C%20MA%2001701&output=embed"
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Serving All of Massachusetts */}
      <ServingMassachusetts />
    </div>
  );
}

function Info({ icon: Icon, label, value, href, testId }) {
  const Content = (
    <div className="flex items-start gap-4">
      <div className="h-10 w-10 rounded-sm bg-white/10 grid place-items-center flex-shrink-0">
        <Icon className="h-4 w-4 text-gold" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold">
          {label}
        </div>
        <div className="text-white mt-1 leading-snug">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block hover:opacity-90">
      {Content}
    </a>
  ) : (
    <div data-testid={testId}>{Content}</div>
  );
}
